import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { IconUpload, IconX } from '@tabler/icons-react';
import UnitSelect from '../../components/UnitSelect';
import { useAuth } from '../../components/AuthContext';
import Overlay from '../../components/Overlay';
import { useTier } from '../../lib/tiers';
import { generateSkuForCategory, nextAvailableSku } from '../../lib/skuGenerator';

// #148 Split A, Session 79.22b.6.1f -- both windows in this file hand-rolled a
// position:fixed backdrop (locked rule 3) and ride with the first session that
// opened the file for UI work: this one, for #357. They move onto Overlay.js
// LIKE FOR LIKE, as the approved drawing (A4) says: the same 480px centred
// window, the same 16px margin on a phone, the same corners. No onClose is
// passed to Overlay, so a tap outside or Escape does NOT close a half-filled
// form -- only Cancel and the X do, exactly as before.
const WINDOW_PANEL = { maxWidth: 'calc(100vw - 32px)', borderRadius: 'var(--radius-lg)', padding: 24 };

// #357, Session 79.22b.6.1f -- the create-flow half. A whole number, 0 or
// more, or blank. Blank stays null, exactly as every new row was before.
function parseReorderPoint(raw) {
  const s = String(raw ?? '').trim();
  if (s === '') return { ok: true, value: null };
  if (!/^\d+$/.test(s)) return { ok: false };
  return { ok: true, value: parseInt(s, 10) };
}
export { parseReorderPoint };

const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];

// UOM_OPTIONS retired at Session 78n2, #398. This file EXPORTED it and the other
// two copies redeclared their own anyway, in different orders - which is the
// clearest single illustration of the problem: the shared source existed and was
// ignored, so a component-level constant was never going to hold the line. The
// list is data now, governed by an executive in Company Settings, and the
// picker reads it. See components/UnitSelect.js.

// ── ADD ITEM MODAL ────────────────────────────────────────────────────────────

export function AddItemModal({ categories, vendors, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', sku: '', category_id: '', uom: 'each', vendor_id: '', description: '' });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();
  const { skuGenerationEnabled } = useAuth();
  // #471, S7. Once somebody types in the SKU box themselves, we stop proposing.
  // Without this the generated value would overwrite their own code every time
  // they touched the name or the category, which reads as the form fighting
  // them - and S7's whole promise is that the code is theirs to override.
  const [skuTouched, setSkuTouched] = useState(false);
  // #357 -- one optional reorder point per branch, keyed by branch id.
  const [reorderAt, setReorderAt] = useState({});
  const { phoneLayout } = useTier();

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  // #471, gate callout A: the code appears WHILE you type, not after you save.
  // It is a field with a value in it rather than a promise about what will
  // happen on submit, so somebody who dislikes it finds out while they can
  // still do something about it.
  useEffect(() => {
    if (!skuGenerationEnabled || skuTouched) return;
    const proposed = generateSkuForCategory({
      categories,
      categoryId: form.category_id,
      name: form.name,
    });
    // Only ever fills the box. An empty proposal - no category chosen yet, or a
    // category with no code - must not wipe what is already showing.
    if (proposed) setForm(f => (f.sku === proposed ? f : { ...f, sku: proposed }));
  }, [form.name, form.category_id, categories, skuGenerationEnabled, skuTouched]);

  async function handleSave() {
    if (!form.name.trim() || !form.sku.trim() || !form.category_id) {
      setError('Name, SKU, and category are required.');
      return;
    }
    // #357 -- checked before anything is written, so a typo never leaves an
    // item created with half its stock rows.
    const reorderByBranch = {};
    for (const b of BRANCHES) {
      const parsed = parseReorderPoint(reorderAt[b.id]);
      if (!parsed.ok) {
        setError(`Reorder at ${b.name} must be a whole number, 0 or more, or blank.`);
        return;
      }
      reorderByBranch[b.id] = parsed.value;
    }
    setSaving(true);
    setError('');
    try {
      let image_url = null;
      if (imageFile) {
        const ext = imageFile.name.split('.').pop();
        const path = `items/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage.from('inventory-assets').upload(path, imageFile, { upsert: true });
        if (upErr) throw upErr;
        const { data: urlData } = supabase.storage.from('inventory-assets').getPublicUrl(path);
        image_url = urlData.publicUrl;
      }
      // #471, S10. Resolved here rather than in skuGenerator.js, which is pure
      // and does not know which database it is writing to. Two unrelated items
      // CAN reach the same base code: inventory_categories has no uniqueness
      // constraint on name (#468's M9), so two parents may each hold a
      // subcategory called Cast.
      //
      // A person who typed their own code gets it left alone - suffixing what
      // somebody deliberately entered would be the form overruling them, and
      // the unique index on inventory_items.sku will reject a true duplicate
      // with an error they can act on.
      let sku = form.sku.trim();
      if (!skuTouched && sku) {
        const { data: clashes } = await supabase
          .from('inventory_items')
          .select('sku')
          .ilike('sku', `${sku}%`);
        sku = nextAvailableSku(sku, (clashes || []).map(r => r.sku));
      }
      const { data: item, error: insErr } = await supabase
        .from('inventory_items')
        .insert({
          name: form.name.trim(),
          sku,
          category_id: parseInt(form.category_id),
          uom: form.uom,
          vendor_id: form.vendor_id ? parseInt(form.vendor_id) : null,
          description: form.description.trim() || null,
          image_url,
          is_active: true,
        })
        .select()
        .single();
      if (insErr) throw insErr;
      const stockRows = BRANCHES.map(b => ({
        item_id: item.id,
        branch_id: b.id,
        qty_on_hand: 0,
        // #357 -- what was typed for this branch, or null as before.
        reorder_point: reorderByBranch[b.id],
        reorder_qty: null,
        location_note: null,
      }));
      const { error: stockErr } = await supabase.from('inventory_stock').insert(stockRows);
      if (stockErr) throw stockErr;
      onSaved(item.id);
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  // #262. Swept at 74s alongside CatalogPromotionModal.js, which is where the reasoning
  // is written out. Two levels; an item's single category_id must point at a subcategory,
  // so parents are group headers only and are never selectable.
  // 74u. Deactivated categories drop out of authoring pickers -- that is what
  // deactivating one is for. They deliberately stay in filters and in label
  // resolution, because items already filed on one must still be findable and
  // must still render their category. Same picker/filter split 74s drew.
  const categoryGroups = categories
    .filter(c => !c.parent_id && c.is_active !== false)
    .map(parent => ({
      parent,
      children: categories.filter(c => c.parent_id === parent.id && c.is_active !== false),
    }));

  return (
    <Overlay variant="modal" maxWidth={480} panelStyle={WINDOW_PANEL}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--black)' }}>Add item</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-mid)' }}><IconX size={18} /></button>
        </div>
        {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 4 }}>Item name *</div>
          <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
            style={{ width: '100%', height: 36, border: '1px solid var(--border)', borderRadius: 8, padding: '0 10px', color: 'var(--black)' }} />
        </div>
        {/* #471. Broken out of the two-field map it used to share with the item
            name, because it now carries a hint and a change handler of its own. */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 4 }}>SKU *</div>
          <input type="text" value={form.sku}
            onChange={e => { setSkuTouched(true); set('sku', e.target.value); }}
            style={{ width: '100%', height: 36, border: '1px solid var(--border)', borderRadius: 8, padding: '0 10px', color: 'var(--black)' }} />
          {/* Gate callout C: the freeze is stated UP FRONT, at the only moment
              the reader cares - while they are deciding whether to accept the
              code. Saying it later, in a warning after a recategorization,
              would be too late to be useful. */}
          {skuGenerationEnabled && !skuTouched && form.sku && (
            <div style={{ fontSize: 11, color: 'var(--gray-mid)', marginTop: 4 }}>
              Suggested from the category and name. Edit it now if you want
              something else &mdash; it is kept as typed and never changes later,
              even if this item moves category.
            </div>
          )}
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 4 }}>Category *</div>
          <select value={form.category_id} onChange={e => set('category_id', e.target.value)}
            style={{ width: '100%', height: 36, border: '1px solid var(--border)', borderRadius: 8, padding: '0 10px', color: 'var(--black)', background: 'white' }}>
            <option value="">Select category</option>
            {categoryGroups.map(({ parent, children }) => (
              <optgroup key={parent.id} label={parent.name}>
                {children.length > 0
                  ? children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)
                  : <option value="" disabled>No subcategories yet</option>}
              </optgroup>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 4 }}>UOM *</div>
          <UnitSelect
            kind="measure"
            value={form.uom}
            onChange={v => set('uom', v)}
            placeholder="pick one"
            ariaLabel="What one of this item is"
            style={{ height: 36, borderRadius: 8, padding: '0 10px' }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 4 }}>Vendor</div>
          <select value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)}
            style={{ width: '100%', height: 36, border: '1px solid var(--border)', borderRadius: 8, padding: '0 10px', color: 'var(--black)', background: 'white' }}>
            <option value="">None</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        {/* #357, Session 79.22b.6.1f -- APPROVED DRAWING A
            (docs/audits/79.22b.6.1f/APPROVED_DRAWING_357_512_199.html). One
            optional box per branch, in ONE row on a phone too: a 2x2 grid adds
            ~141px and pushes Save item off an 812px screen (A3). Per branch
            because a reorder point belongs to a branch (A1); blank stays null, so
            an item nobody sets a threshold for behaves exactly as before (A2).
            The 9px gap below is the drawing's own, and it matters: at the form's
            usual 12px the window measured 731px against the 729px an 812px
            phone leaves, and the drawing promised no scrolling there. */}
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 4 }}>Reorder at (optional)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: phoneLayout ? 5 : 8 }}>
            {BRANCHES.map(b => (
              <label key={b.id} style={{ display: 'block', minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 11, color: '#5C5C5C', whiteSpace: 'nowrap', marginBottom: 2 }}>{b.name}</span>
                <input type="number" min={0} step={1} inputMode="numeric"
                  aria-label={`Reorder at ${b.name}`}
                  value={reorderAt[b.id] ?? ''}
                  onChange={e => setReorderAt(r => ({ ...r, [b.id]: e.target.value }))}
                  style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', height: 36, border: '1px solid var(--border)', borderRadius: 8, padding: '0 8px', fontSize: 16, color: 'var(--black)' }} />
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#6B6B6B', marginTop: 4 }}>
            Shows Low at a branch when stock falls to this. Leave a branch blank if it doesn&rsquo;t stock it.
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 4 }}>Description</div>
          <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3}
            style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', color: 'var(--black)', resize: 'vertical' }} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 4 }}>Image (optional)</div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setImageFile(e.target.files[0] || null)} />
          <button onClick={() => fileRef.current.click()}
            style={{ height: 36, border: '1px solid var(--border)', borderRadius: 8, padding: '0 14px', fontSize: 13, background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconUpload size={14} /> {imageFile ? imageFile.name : 'Choose image'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn btn-primary btn-sm">{saving ? 'Saving...' : 'Save item'}</button>
        </div>
    </Overlay>
  );
}

// ── EDIT SPECS MODAL ──────────────────────────────────────────────────────────

export function EditSpecsModal({ item, categories, vendors, onClose, onSaved }) {
  // #471, S9. The category this item was filed under when the screen opened.
  // Held rather than compared against `item.category_id` directly so the notice
  // is about what THIS EDIT changes, not about anything that happened before.
  const originalCategoryId = item.category_id ? String(item.category_id) : '';
  const [form, setForm] = useState({
    name: item.name || '',
    sku: item.sku || '',
    category_id: item.category_id ? String(item.category_id) : '',
    uom: item.uom || 'each',
    vendor_id: item.vendor_id ? String(item.vendor_id) : '',
    description: item.description || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  async function handleSave() {
    if (!form.name.trim() || !form.sku.trim() || !form.category_id) {
      setError('Name, SKU, and category are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { error: updErr } = await supabase
        .from('inventory_items')
        .update({
          name: form.name.trim(),
          sku: form.sku.trim(),
          category_id: parseInt(form.category_id),
          uom: form.uom,
          vendor_id: form.vendor_id ? parseInt(form.vendor_id) : null,
          description: form.description.trim() || null,
        })
        .eq('id', item.id);
      if (updErr) throw updErr;
      onSaved();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  // #262. Swept at 74s alongside CatalogPromotionModal.js, which is where the reasoning
  // is written out. Two levels; an item's single category_id must point at a subcategory,
  // so parents are group headers only and are never selectable.
  // 74u. Deactivated categories drop out of authoring pickers -- that is what
  // deactivating one is for. They deliberately stay in filters and in label
  // resolution, because items already filed on one must still be findable and
  // must still render their category. Same picker/filter split 74s drew.
  const categoryGroups = categories
    .filter(c => !c.parent_id && c.is_active !== false)
    .map(parent => ({
      parent,
      children: categories.filter(c => c.parent_id === parent.id && c.is_active !== false),
    }));

  const categoryChanged = String(form.category_id || '') !== originalCategoryId;

  return (
    <Overlay variant="modal" maxWidth={480} panelStyle={WINDOW_PANEL}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--black)' }}>Edit specs</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-mid)' }}><IconX size={18} /></button>
        </div>
        {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}
        {[
          { label: 'Item name *', field: 'name' },
          { label: 'SKU *',       field: 'sku'  },
        ].map(({ label, field }) => (
          <div key={field} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
            <input type="text" value={form[field]} onChange={e => set(field, e.target.value)}
              style={{ width: '100%', height: 36, border: '1px solid var(--border)', borderRadius: 8, padding: '0 10px', color: 'var(--black)' }} />
          </div>
        ))}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 4 }}>Category *</div>
          <select value={form.category_id} onChange={e => set('category_id', e.target.value)}
            style={{ width: '100%', height: 36, border: '1px solid var(--border)', borderRadius: 8, padding: '0 10px', color: 'var(--black)', background: 'white' }}>
            <option value="">Select category</option>
            {categoryGroups.map(({ parent, children }) => (
              <optgroup key={parent.id} label={parent.name}>
                {children.length > 0
                  ? children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)
                  : <option value="" disabled>No subcategories yet</option>}
              </optgroup>
            ))}
          </select>
          {/* #471, S9 and gate callout K -- the recategorization notice.
              IT STATES A FACT, IT DOES NOT ASK A QUESTION. There is deliberately
              no "regenerate the SKU?" option here, because offering the choice
              would reintroduce exactly what S8 exists to remove, and somebody
              would eventually take it. Regenerating invalidates every printed
              label already stuck to a shelf.
              Non-blocking, and shown only when it is TRUE: the category has
              actually been changed, and this item has a code to keep. */}
          {categoryChanged && form.sku.trim() && (
            <div style={{ fontSize: 12, color: 'var(--gray-mid)', marginTop: 6, padding: '8px 10px', background: '#F5F5F3', borderRadius: 8 }}>
              This item&rsquo;s SKU stays{' '}
              <strong style={{ color: 'var(--black)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                {form.sku.trim()}
              </strong>. It was assigned when the item was created and does not
              change &mdash; printed labels and past orders still refer to it.
            </div>
          )}
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 4 }}>UOM *</div>
          <UnitSelect
            kind="measure"
            value={form.uom}
            onChange={v => set('uom', v)}
            placeholder="pick one"
            ariaLabel="What one of this item is"
            style={{ height: 36, borderRadius: 8, padding: '0 10px' }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 4 }}>Vendor</div>
          <select value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)}
            style={{ width: '100%', height: 36, border: '1px solid var(--border)', borderRadius: 8, padding: '0 10px', color: 'var(--black)', background: 'white' }}>
            <option value="">None</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 4 }}>Description</div>
          <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3}
            style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', color: 'var(--black)', resize: 'vertical' }} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn btn-primary btn-sm">{saving ? 'Saving...' : 'Save changes'}</button>
        </div>
    </Overlay>
  );
}